import { listProgramsOptions } from "@/actions/program/handler";
import {
  assignProgramFn,
  getActiveUserProgramOptions,
} from "@/actions/user-program/handler";
import type { EssayCalculatorType } from "@/api/db/schema/essay";
import { authClient } from "@/auth/auth-client";
import { trackProgramStart } from "@/components/analytics/conversions";
import { ProgramActivationWizard } from "@/components/program/ProgramActivationWizard";
import { Button } from "@/components/ui/button";
import { getCalculatorCatalogItem } from "@/utils/calculator-catalog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Calculator, Dumbbell, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

interface EssayCtaProps {
  programId?: string | null;
  calculatorId?: EssayCalculatorType | null;
  label?: string | null;
}

export function EssayCta({ programId, calculatorId, label }: EssayCtaProps) {
  if (programId) {
    return <ProgramEssayCta programId={programId} label={label} />;
  }

  const calculator = getCalculatorCatalogItem(calculatorId);
  if (!calculator) return null;

  const Icon = calculator.icon;
  return (
    <section className="mt-10 overflow-hidden rounded-2xl border border-primary/20 bg-primary/5 p-5 sm:p-6">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold text-primary text-xs uppercase tracking-wider">
              Sıradaki Adım
            </p>
            <h2 className="mt-1 font-bold text-xl">{calculator.title}</h2>
            <p className="mt-1 text-muted-foreground text-sm leading-relaxed">
              {calculator.description}
            </p>
          </div>
        </div>
        <Button asChild className="shrink-0 rounded-xl">
          <Link to={calculator.href} data-analytics-cta="essay_calculator_cta">
            <Calculator className="mr-2 h-4 w-4" />
            {label?.trim() || calculator.defaultCtaLabel}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </section>
  );
}

function ProgramEssayCta({
  programId,
  label,
}: { programId: string; label?: string | null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data: session } = authClient.useSession();
  const { data: programsData, isPending } = useQuery(listProgramsOptions());
  const { data: activeData } = useQuery({
    ...getActiveUserProgramOptions(),
    enabled: Boolean(session?.user),
  });
  const program = (programsData?.body?.programs ?? []).find(
    (candidate) => candidate.programId === programId,
  );
  const activeProgram =
    activeData?.body && "programId" in activeData.body ? activeData.body : null;

  const assignMutation = useMutation({
    mutationFn: assignProgramFn,
    onSuccess: async (result) => {
      if (result.status !== 201 || !program) {
        toast.error(
          result.body && "message" in result.body
            ? result.body.message
            : "Program başlatılamadı.",
        );
        return;
      }
      trackProgramStart(program.programId, program.name);
      await queryClient.invalidateQueries({
        queryKey: getActiveUserProgramOptions().queryKey,
      });
      navigate({
        to: "/program/$programId",
        params: { programId: program.programId },
      });
    },
    onError: () =>
      toast.error("Program başlatılamadı. Tekrar deneyebilirsiniz."),
  });

  if (isPending) {
    return (
      <section className="mt-10 rounded-2xl border bg-muted/20 p-6 text-muted-foreground text-sm">
        Program bilgileri yükleniyor...
      </section>
    );
  }
  if (!program) return null;

  const isCurrentProgram = activeProgram?.programId === program.programId;
  const isPaidProgram = Boolean(
    program.requiresPurchase ||
      program.isPaid ||
      (program.price && program.price > 0) ||
      program.accessCode,
  );
  const hasAccess = program.hasAccess ?? !isPaidProgram;
  const needsPurchase = isPaidProgram && !hasAccess;
  const description =
    program.catalogDescription ||
    program.description ||
    "Programı inceleyin ve hedefiniz için bir sonraki adımı atın.";

  const action = isCurrentProgram ? (
    <Button asChild className="shrink-0 rounded-xl">
      <Link
        to="/program/$programId"
        params={{ programId: program.programId }}
        data-analytics-cta="essay_program_cta"
      >
        Devam Et <ArrowRight className="ml-2 h-4 w-4" />
      </Link>
    </Button>
  ) : needsPurchase ? (
    <Button asChild className="shrink-0 rounded-xl">
      <Link
        to="/program/$programId"
        params={{ programId: program.programId }}
        data-analytics-cta="essay_program_cta"
      >
        <ShoppingBag className="mr-2 h-4 w-4" />
        İncele & Satın Al
      </Link>
    </Button>
  ) : (
    <ProgramActivationWizard
      programName={program.catalogName || program.name}
      isPending={assignMutation.isPending}
      onConfirm={() =>
        assignMutation.mutate({ data: { programId: program.programId } })
      }
      hasActiveProgram={Boolean(activeProgram)}
      loginRedirect={location.href}
    >
      <Button
        disabled={assignMutation.isPending}
        className="shrink-0 rounded-xl"
        data-analytics-cta="essay_program_cta"
        data-analytics-destination={`/program/${program.programId}`}
      >
        {assignMutation.isPending
          ? "Başlatılıyor..."
          : label?.trim() || "Programı Başlat"}
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </ProgramActivationWizard>
  );

  return (
    <section className="mt-10 overflow-hidden rounded-2xl border border-primary/20 bg-primary/5 p-5 sm:p-6">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Dumbbell className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold text-primary text-xs uppercase tracking-wider">
              Sıradaki Adım
            </p>
            <h2 className="mt-1 font-bold text-xl">
              {program.catalogName || program.name}
            </h2>
            <p className="mt-1 line-clamp-2 text-muted-foreground text-sm leading-relaxed">
              {description}
            </p>
          </div>
        </div>
        {action}
      </div>
    </section>
  );
}
